import { useEffect, useRef, useState } from 'react';
import { useGetSectionByIdQuery } from '@/services/plaintiffSheets/plaintiffSheetsEndpoint';
import Certifications, { CertificationsHandle } from '../user/certifications/certifications';
import Button from '@/components/Button/Button';
import CustomTextInput from '@/pages/plaintiffSheets/components/InputFields/CustomTextInput';
import UserCustomerTextArea from '@/pages/plaintiffSheets/components/InputFields/UserCustomerTextArea';
import CustomConditionInput from '@/pages/plaintiffSheets/components/InputFields/CustomConditionInput';
import CustomInformationField from '@/pages/plaintiffSheets/components/InputFields/CustomInformationInput';
import CustomDropdownInput from '@/pages/plaintiffSheets/components/InputFields/CustomDropdownInput';
import CustomDateInput from '@/pages/plaintiffSheets/components/InputFields/CustomDateInput';
import CustomDataTable from '@/pages/plaintiffSheets/components/InputFields/CustomDataTable';
import { useDispatch, useSelector } from 'react-redux';
import CustomTabsWithTable from '@/pages/plaintiffSheets/components/InputFields/CustomTabsWithTable';
import CustomLabel from '@/pages/plaintiffSheets/components/InputFields/CustomLabel';
import { setHasUnsavedChanges } from '@/reducers/formSlice';

import ErrorPage from '../user/errorPage/ErrorPage';
import { RootState } from '@/store';
import CustomMultipleField from '@/pages/plaintiffSheets/components/InputFields/CustomMultipleField';
import { setDataTableUpdated } from '@/reducers/dataTableUpdatedSlice';
import { setSectionId } from '@/reducers/sectionIdSlice';
import CustomReadTextArea from '@/pages/plaintiffSheets/components/InputFields/CustomReadTextArea';
import CustomFileUpload from '@/pages/plaintiffSheets/components/InputFields/CustomeFileUpload';
import Loader from '@/components/Loader/Loader';
import { useLocation, useNavigate } from 'react-router-dom';
import { setReviewForm } from '@/reducers/reviewFormSlice';

interface PreviewViewFormProps {
  selectDistrict: string;
  isSuccess?: boolean;
  sections: any;
  districtName: string;
}

const PreviewViewForm = ({
  selectDistrict,
  isSuccess,
  sections,
  districtName,
}: PreviewViewFormProps) => {
  const sectionId = useSelector((state: any) => state.sectionId.sectionId);
  const location = useLocation();
  const navigate = useNavigate();

  const certificationsRef = useRef<CertificationsHandle>(null);

  const isReviewForm = useSelector((state: RootState) => state.reviewForm.isReviewForm);

  const factSheetStatus = useSelector((state: RootState) => state.factSheetStatus.status);

  const certificationDisabled = useSelector(
    (state: RootState) => state?.progress.progressCheck || false
  );

  const {
    data: fieldData,
    isFetching: isApiFetching,
    isLoading: isFieldDataLoading,
    isError,
  } = useGetSectionByIdQuery(
    {
      districtId: selectDistrict,
      sectionId: sectionId,
    },
    {
      skip: !sectionId || !selectDistrict || !isSuccess,
      refetchOnMountOrArgChange: true,
    }
  );

  const isPreview = location.pathname.includes('preview');

  useEffect(() => {
    if (isPreview) dispatch(setReviewForm(true));
  }, [navigate]);
  // Tab Title
  const title = fieldData?.data?.name;
  useEffect(() => {
    if (title) {
      document.title = `${title} | Plaintiff Fact Sheet`;
    }
  }, [title]);

  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const formPages: { section_id: string }[] = sections || [];

  // Data Type
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // Get all fields and tables, then sort them by order
  const allFields = fieldData?.data?.fields || [];
  const dataTables = fieldData?.data?.data_tables || [];
  const tabsTables = fieldData?.data?.tabs || [];

  // Filter main fields (non-conditional)
  const mainFields = allFields.filter((field: any) => field.fieldScope !== 'condition');

  // Combine fields, data tables, and tabs tables into a single array for ordered rendering
  const allComponents = [...mainFields, ...dataTables, ...tabsTables].sort(
    (a, b) => a.order - b.order
  );

  // For fields that are not conditional, set their initial values
  useEffect(() => {
    if (!allFields.length) return;

    allFields.forEach((field: any) => {
      formValues[field.field_id] = field.value || '';
    });
  }, [fieldData, isApiFetching]);

  // ----------------------------Handle change events for the data types ----------------------------- //
  const handleChange = (fieldId: string) => (e: React.ChangeEvent<any>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormValues((prev) => {
      const updated = { ...prev, [fieldId]: value };
      return updated;
    });
  };

  const handletextAreaChange = (fieldId: string) => (e: React.ChangeEvent<any>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    dispatch(setHasUnsavedChanges(true));
    dispatch(setDataTableUpdated(false));
    setFormValues((prev) => {
      const updated = { ...prev, [fieldId]: value };
      return updated;
    });
  };

  const handleDateOrDropdownChange =
    (fieldId: string) => (e: { target: { value: string; name: string; type: string } }) => {
      const value = e.target.type === 'checkbox' ? e.target.value == 'true' : e.target.value;
      dispatch(setHasUnsavedChanges(true));
      dispatch(setDataTableUpdated(false));
      setFormValues((prev) => {
        const updated = { ...prev, [fieldId]: value };
        return updated;
      });
    };

  const renderField = (field: any) => {
    switch (field.field_type) {
      case 'text_input':
        return (
          <CustomTextInput
            label={field.label}
            name={field.field_id}
            value={
              field?.label?.includes('1. Plaintiff')
                ? districtName
                : formValues[field.field_id]
                  ? formValues[field.field_id]
                  : ''
            }
            onChange={handleChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || ''}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[350px]"
            isEditable={field.additional_config?.editable}
            defaultValue={field?.additional_config?.defaultValue}
            className="p-1"
            isReviewForm={isReviewForm}
          />
        );

      case 'text_area':
        return (
          <>
            <UserCustomerTextArea
              label={field.label}
              name={field.field_id}
              value={formValues[field.field_id] ?? ''}
              onChange={handletextAreaChange(field.field_id)}
              placeholder={field.additional_config?.placeholder || ''}
              required={field.is_required}
              description={field.additional_config?.helpText}
              rows={8}
              isCondition={false}
              isReviewForm={isReviewForm}
            />
          </>
        );
      case 'dropdown':
        return (
          <CustomDropdownInput
            label={field.label}
            name={field.field_id}
            value={formValues[field.field_id] ?? ''}
            options={field.options || []}
            onChange={handleDateOrDropdownChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || 'Select an option'}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[400px]"
            isEditable={field.additional_config?.editable !== false}
            className="p-1"
          />
        );
      case 'date':
        return (
          <CustomDateInput
            label={field.label}
            name={field.field_id}
            value={formValues[field.field_id] ?? ''}
            onChange={handleDateOrDropdownChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || 'Select Date'}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[400px]"
            isEditable={field.additional_config?.editable !== false}
            className="p-1"
            dateFormat={field.additional_config?.dateFormat || 'dd-mm-yyyy'}
          />
        );

      case 'information':
        return (
          <CustomInformationField
            label={field.label}
            sectionName={fieldData?.data?.name}
            description={field.additional_config?.helpText}
            beforeLinkDescription={field.additional_config?.beforeLinkDescription}
            inlineTriggerText={field.additional_config?.inlineTriggerText}
            modalContent={field.additional_config?.modalContent}
            table_response={field.table_responses}
          />
        );
      case 'condition':
      case 'condition_with_table':
        return (
          <CustomConditionInput
            sectionData={fieldData?.data}
            label={field.label}
            options={field.options}
            required={field.is_required}
            description={field.additional_config?.helpText}
            isReviewForm={isReviewForm}
            defaultValue={field?.additional_config?.defaultValue}
            value={field.value}
            isLockedByMe={false}
            lockedOwner={''}
          />
        );

      case 'file':
        return (
          <CustomFileUpload
            label={field.label}
            value={fieldData}
            name={field.label}
            isReviewForm={isReviewForm}
            values={field.files}
            fieldId={field.field_id}
          />
        );

      case 'condition_with_multiple_fields':
        return (
          <CustomMultipleField
            fieldId={field?.field_id}
            value={field.value}
            name={field.label}
            label={field?.label}
            options={field?.options}
            isReviewForm={isReviewForm}
            sectionData={fieldData}
            defaultValue={field?.additional_config?.defaultValue}
            helpText={field?.additional_config?.helpText}
          />
        );

      case 'readonly_text_area':
        return <CustomReadTextArea label={field.label} data={field} />;

      case 'label':
        return <CustomLabel name={field.label} />;

      default:
        return <p className="text-red-500">Unsupported field type: {field.fieldType}</p>;
    }
  };

  // ---------------------------- Rendering the fields based on their type ------------------------------- //

  const handleNext = () => {
    if (currentStep < formPages.length - 1) {
      dispatch(setSectionId(formPages[currentStep + 1].section_id));
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // Update section ID and navigate
      dispatch(setSectionId(formPages[currentStep - 1].section_id));
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
    }
  };

  const shouldBeInGrid = (fieldType: string) => {
    return ['text_input', 'dropdown', 'date'].includes(fieldType);
  };

  if (isError) {
    return <ErrorPage />;
  }

  return (
    <>
      {isFieldDataLoading || isApiFetching ? (
        <div className="flex justify-center items-center h-full w-full">
          <Loader color="primary" size="xl" />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Show Data based on locked user  */}
          <div className={`relative overflow-y-auto h-[80%]  m-2 my-4 py-2`}>
            <div className={` my-2 py-6 rounded-lg`}>
              <div className="h-full w-full  ">
                <div className="font-semibold flex flex-col gap-1 text-ds-gray-800 border-b py-2 mb-2 w-full border-blue-2100">
                  <h1>{fieldData?.data?.name ?? ''}</h1>
                  <h2 className="text-ds-gray-400 text-xs font-normal">
                    {fieldData?.data?.description}
                  </h2>
                </div>
                {/* Making Hard coded Instructions and Submission section */}
                {fieldData?.data?.name === 'Submission' ? (
                  <Certifications ref={certificationsRef} factSheetStatus={factSheetStatus} />
                ) : (
                  <div className="space-y-6 gap-2">
                    {/* Rendering logic for components with proper type detection */}
                    {(() => {
                      const elements = [];
                      let gridFieldsGroup = [];

                      for (const component of allComponents) {
                        if (shouldBeInGrid(component.field_type)) {
                          // Add field to the current group
                          gridFieldsGroup.push(component);

                          // When we have 2 grid fields, render them in a row
                          if (gridFieldsGroup.length === 2) {
                            elements.push(
                              <div
                                key={`group-${gridFieldsGroup[0].field_id}`}
                                className="grid grid-cols-1 2xl:grid-cols-2 md:grid-cols-1 gap-2"
                              >
                                {gridFieldsGroup.map((input) => (
                                  <div key={input.field_id}>{renderField(input)}</div>
                                ))}
                              </div>
                            );
                            gridFieldsGroup = [];
                          }
                        } else {
                          // If we have pending grid fields (1 remaining), render them first
                          if (gridFieldsGroup.length > 0) {
                            elements.push(
                              <div
                                key={`group-${gridFieldsGroup[0].field_id}`}
                                className="grid grid-cols-2 xl:grid-cols-2 lg:grid-cols-1 gap-4 mr-10"
                              >
                                {gridFieldsGroup.map((input) => (
                                  <div key={input.field_id}>{renderField(input)}</div>
                                ))}
                              </div>
                            );
                            gridFieldsGroup = [];
                          }

                          // Render non-grid components based on their type
                          if (component.field_type) {
                            // This is a regular field
                            elements.push(
                              <div key={component.field_id} className="col-span-3">
                                {renderField(component)}
                              </div>
                            );
                          } else if (component?.table_id) {
                            // This is a data table
                            elements.push(
                              <div
                                key={component.field_id || `table-${component.id}`}
                                className="w-full mt-6"
                              >
                                <CustomDataTable
                                  tableData={component}
                                  isReviewForm={isReviewForm}
                                  lockedOwner=""
                                  isLockedByMe={false}
                                />
                              </div>
                            );
                          } else if (component?.tab_field_id) {
                            // This is a tabs table
                            elements.push(
                              <div
                                key={component.id || `tabs-${elements.length}`}
                                className=" w-full mt-6"
                              >
                                <CustomTabsWithTable
                                  formData={component}
                                  isReviewForm={isReviewForm}
                                  lockedOwner=""
                                  isLockedByMe={false}
                                />
                              </div>
                            );
                          }
                        }
                      }

                      // Render any remaining grid fields
                      if (gridFieldsGroup.length > 0) {
                        elements.push(
                          <div
                            key={`group-${gridFieldsGroup[0].field_id}`}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                          >
                            {gridFieldsGroup.map((input) => (
                              <div key={input.field_id}>{renderField(input)}</div>
                            ))}
                          </div>
                        );
                      }

                      return elements;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Unsaved Progress Popup */}

          {/* Footer buttons */}
          <div className="flex justify-end px-4  border-t h-16 my-auto">
            <div className="flex gap-2 items-center my-auto">
              {/* For Dynamic Sections  */}

              <Button
                size="sm"
                onClick={() => {
                  handlePrevious();
                }}
                color="outlined"
              >
                Back
              </Button>

              <Button
                onClick={() => {
                  handleNext();
                }}
                size="sm"
                color="primary"
                disabled={
                  fieldData?.data.name == 'Supplemental Questions' ? certificationDisabled : false
                }
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Add display name
PreviewViewForm.displayName = 'PreviewViewForm';

export default PreviewViewForm;
